import React from 'react';
import { useForm } from 'react-hook-form';
import { FIELD_TYPE } from '../../constants';
import { UploadFieldConfig } from './config-types';

export type MarketplaceUploadFormData = {
    [key: string]: string | number | boolean;
};

type MarketplaceUploadFormProps = {
    onSubmit: (values: MarketplaceUploadFormData) => void;
    children: React.ReactNode;
    className?: string;
    uploadedFilesCount: number;
    formError: string | null;
    fields: UploadFieldConfig[];
    title?: string;
};

export const MarketplaceUploadForm: React.FC<MarketplaceUploadFormProps> = (
    { onSubmit, children, className, uploadedFilesCount, formError, fields, title }
) => {
    
    const [showFileError, setShowFileError] = React.useState(false);

    React.useEffect(() => {
        if (uploadedFilesCount > 0) {
            setShowFileError(false);
        }
    }, [uploadedFilesCount]);

    const { register, handleSubmit, formState: { errors } } = useForm<MarketplaceUploadFormData>({});

    const getValidationRules = (field: UploadFieldConfig) => {
        const rules: any = {
            required: field.required ? 'This field is required' : false,
        };

        switch (field.type) {
            case FIELD_TYPE.NUMBER:
                rules.pattern = {
                    value: /^-?\d*\.?\d*$/,
                    message: 'Please enter a valid number'
                };
                if (field.parameters?.min !== undefined) {
                    rules.min = {
                        value: field.parameters.min,
                        message: `Minimum value is ${field.parameters.min}`
                    };
                }
                if (field.parameters?.max !== undefined) {
                    rules.max = {
                        value: field.parameters.max,
                        message: `Maximum value is ${field.parameters.max}`
                    };
                }
                break;

            case FIELD_TYPE.DATE:
            case FIELD_TYPE.DATETIME:
            case FIELD_TYPE.TIME:
                rules.pattern = {
                    value: field.type === FIELD_TYPE.TIME ? /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/ : undefined,
                    message: 'Please enter a valid time (HH:MM)'
                };
                break;
        }

        return rules;
    };

    const renderField = (field: UploadFieldConfig) => {
        const fieldName = field.name;

        switch (field.type) {
            case FIELD_TYPE.TEXT:
                return (
                    <textarea
                        id={fieldName}
                        className="form__input"
                        {...register(fieldName, getValidationRules(field))}
                    />
                );

            case FIELD_TYPE.DATE:
                return (
                    <input
                        type="date"
                        id={fieldName}
                        className="form__input"
                        {...register(fieldName, getValidationRules(field))}
                    />
                );

            case FIELD_TYPE.TIME:
                return (
                    <input
                        type="time"
                        id={fieldName}
                        className="form__input"
                        {...register(fieldName, getValidationRules(field))}
                    />
                );

            case FIELD_TYPE.DATETIME:
                return (
                    <input
                        type="datetime-local"
                        id={fieldName}
                        className="form__input"
                        {...register(fieldName, getValidationRules(field))}
                    />
                );

            case FIELD_TYPE.NUMBER:
                return (
                    <input
                        type="number"
                        id={fieldName}
                        className="form__input"
                        step="any"
                        {...register(fieldName, getValidationRules(field))}
                    />
                );

            case FIELD_TYPE.CHOICE:
                return (
                    <select
                        id={fieldName}
                        className="form__input"
                        {...register(fieldName, getValidationRules(field))}
                    >
                        <option value="">Select an option</option>
                        {(field.parameters?.choices || []).map((choice: string) => (
                            <option key={choice} value={choice}>
                                {choice}
                            </option>
                        ))}
                    </select>
                );

            default:
                return (
                    <input
                        type="text"
                        id={fieldName}
                        className="form__input"
                        {...register(fieldName, getValidationRules(field))}
                    />
                );
        }
    };

    const onFormSubmit = (data: MarketplaceUploadFormData) => {
        if (uploadedFilesCount === 0) {
            setShowFileError(true);
            return;
        }
        
        onSubmit(data);
    };

    return (
        <form onSubmit={handleSubmit(onFormSubmit)} className={`${className} form`}>
             {formError && (
                <div className="form__row is-error">
                    <div className="form__alert">
                        {formError}
                    </div>
                </div>
            )}
            {title && (
                <h2 className="upload__storytitle-title">{title}</h2>
            )}
            {fields.map((field) => {
                const fieldName = field.name;
                return (
                    <div key={fieldName} className="upload__storytitle form">
                        <div className={`form__row ${errors[fieldName] ? 'is-error' : ''}`}>
                            <label className="form__label" htmlFor={fieldName}>
                                {field.label}
                            </label>
                            <div className="form__input-holder">
                                {renderField(field)}
                            </div>
                            {errors[fieldName] && (
                                <div className="form__alert">
                                    {errors[fieldName]?.message as string}
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}

           
            {(showFileError && uploadedFilesCount === 0) && (
                <div className="form__row is-error">
                    <div className="form__alert">
                        Please upload at least one image
                    </div>
                </div>
            )}

            {children}
        </form>
    );
};

export default MarketplaceUploadForm;